import * as THREE from 'three/webgpu';
import type { AbstractEntitySelectionHandler } from '../../manager/entitySelection/AbstractEntitySelectionHandler';
import { ViewerMode } from '../../viewer/ViewerMode';
import type { AssemblyEntitySelectionHandler } from '../../manager/entitySelection/AssemblyEntitySelectionHandler';
import type { AssemblyReferenceAxisEntitySelectionHandler } from '../../manager/entitySelection/AssemblyReferenceAxisEntitySelectionHandler';
import type { AssemblyReferencePlaneEntitySelectionHandler } from '../../manager/entitySelection/AssemblyReferencePlaneEntitySelectionHandler';
import type { AssemblyReferencePointEntitySelectionHandler } from '../../manager/entitySelection/AssemblyReferencePointEntitySelectionHandler';
import type { ReferenceAxisEntitySelectionHandler } from '../../manager/entitySelection/ReferenceAxisEntitySelectionHandler';
import type { ReferencePlaneEntitySelectionHandler } from '../../manager/entitySelection/ReferencePlaneEntitySelectionHandler';
import type { ReferencePointEntitySelectionHandler } from '../../manager/entitySelection/ReferencePointEntitySelectionHandler';
import type { SketchEntitySelectionHandler } from '../../manager/entitySelection/SketchEntitySelectionHandler';
import {
    MeasurePickScopeFamily,
    resolveMeasurePickScopeFamily,
} from './MeasurePickScopeFamily';

/**
/**
 * 测量拾取候选集解析器依赖。
 *
 * 术语说明：“候选集”指的是在SolidWorks中测量功能启动时，根据当前编辑模式（如零件、装配体、草图或基准等）屏蔽无关对象后，仅允许用户实际进行拾取（可点击或高亮）的那批场景对象集合。简单来说，就是“允许被测量选择的对象全集”。
 * 这些对象会作为候选目标参与后续的射线检测（Raycast），其范围由当前会话模式的 SelectionHandler 模块统一决策，从而保证在不同命令/状态下的测量目标范围与SolidWorks 100%一致。例如，在装配体命令期只允许拾取零部件和基准几何，而草图模式下则限定为草图几何等。
 */
export interface MeasurePickScopeResolverDependencies {
    /** 零件空闲态选择处理器（当前复用 Assembly 处理器实例）。 */
    defaultModeSelectionHandler: AssemblyEntitySelectionHandler;
    /** 装配体选择处理器。 */
    assemblySelectionHandler: AssemblyEntitySelectionHandler;
    /** 草图选择处理器。 */
    sketchSelectionHandler: SketchEntitySelectionHandler;
    /** 基准面选择处理器。 */
    referencePlaneSelectionHandler: ReferencePlaneEntitySelectionHandler;
    /** 基准轴选择处理器。 */
    referenceAxisSelectionHandler: ReferenceAxisEntitySelectionHandler;
    /** 基准点选择处理器。 */
    referencePointSelectionHandler: ReferencePointEntitySelectionHandler;
    /** 装配体基准面选择处理器。 */
    assemblyReferencePlaneSelectionHandler: AssemblyReferencePlaneEntitySelectionHandler;
    /** 装配体基准轴选择处理器。 */
    assemblyReferenceAxisSelectionHandler: AssemblyReferenceAxisEntitySelectionHandler;
    /** 装配体基准点选择处理器。 */
    assemblyReferencePointSelectionHandler: AssemblyReferencePointEntitySelectionHandler;
}

/**
 * 测量拾取候选集解析器。
 *
 * 职责：按进入测量前记录的底层模式，委托对应 Handler 的候选集规则。
 */
export class MeasurePickScopeResolver {
    /** 候选集处理器依赖。 */
    private readonly dependencies: MeasurePickScopeResolverDependencies;

    /**
     * @param dependencies 候选集处理器依赖。
     */
    public constructor(dependencies: MeasurePickScopeResolverDependencies) {
        this.dependencies = dependencies;
    }

    /**
     * 解析测量点击候选集。
     *
     * @param baseViewerMode 测量会话底层模式。
     * @returns 可参与射线检测的对象列表。
     */
    public resolveClickTargets(baseViewerMode: ViewerMode): THREE.Object3D[] {
        return this.resolveTargets(baseViewerMode, 'click');
    }

    /**
     * 解析测量移入候选集。
     *
     * @param baseViewerMode 测量会话底层模式。
     * @returns 可参与射线检测的对象列表。
     */
    public resolveHoverTargets(baseViewerMode: ViewerMode): THREE.Object3D[] {
        return this.resolveTargets(baseViewerMode, 'hover');
    }

    /**
     * 按底层模式执行测量射线拾取。
     *
     * 说明：委托对应 Handler 的 `pickMeasureEntityAt`，复用 imported-model / 基准对象等模式专属拾取链。
     *
     * @param baseViewerMode 测量会话底层模式。
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @param intent 拾取意图（点击 / 移入）。
     * @returns 命中结果；未命中时返回 null。
     */
    public pickEntityAt(
        baseViewerMode: ViewerMode,
        clientX: number,
        clientY: number,
        intent: 'click' | 'hover',
    ): THREE.Intersection<THREE.Object3D> | null {
        const handler = this.resolvePickHandler(baseViewerMode);
        const targets = intent === 'click'
            ? this.resolveClickTargets(baseViewerMode)
            : this.resolveHoverTargets(baseViewerMode);
        return handler.pickMeasureEntityAt(clientX, clientY, targets);
    }

    /**
     * 按底层模式解析测量点击命中。
     *
     * @param baseViewerMode 测量会话底层模式。
     * @param picked 原始命中结果。
     * @returns 点击选择应使用的命中结果。
     */
    public resolveClickPick(
        baseViewerMode: ViewerMode,
        picked: THREE.Intersection<THREE.Object3D> | null,
    ): THREE.Intersection<THREE.Object3D> | null {
        return this.resolvePickHandler(baseViewerMode).resolveMeasureClickPick(picked);
    }

    /**
     * 按模式族解析候选集。
     *
     * @param baseViewerMode 测量会话底层模式。
     * @param intent 拾取意图（点击 / 移入）。
     * @returns 候选对象列表。
     */
    private resolveTargets(
        baseViewerMode: ViewerMode,
        intent: 'click' | 'hover',
    ): THREE.Object3D[] {
        const scopeFamily = resolveMeasurePickScopeFamily(baseViewerMode);

        switch (scopeFamily) {
            case MeasurePickScopeFamily.PartIdle:
                return intent === 'click'
                    ? this.dependencies.defaultModeSelectionHandler.collectMeasureClickSelectionTargets()
                    : this.dependencies.defaultModeSelectionHandler.collectMeasureHoverSelectionTargets();
            case MeasurePickScopeFamily.Assembly:
                return intent === 'click'
                    ? this.dependencies.assemblySelectionHandler.collectMeasureClickSelectionTargets()
                    : this.dependencies.assemblySelectionHandler.collectMeasureHoverSelectionTargets();
            case MeasurePickScopeFamily.Sketch:
                return intent === 'click'
                    ? this.dependencies.sketchSelectionHandler.collectMeasureClickSelectionTargets()
                    : this.dependencies.sketchSelectionHandler.collectMeasureHoverSelectionTargets();
            case MeasurePickScopeFamily.Reference:
                return this.resolveReferenceTargets(baseViewerMode, intent);
            case MeasurePickScopeFamily.AssemblyReference:
                return this.resolveAssemblyReferenceTargets(baseViewerMode, intent);
            default:
                return intent === 'click'
                    ? this.dependencies.defaultModeSelectionHandler.collectMeasureClickSelectionTargets()
                    : this.dependencies.defaultModeSelectionHandler.collectMeasureHoverSelectionTargets();
        }
    }

    /**
     * 该方法用于根据当前的底层查看模式（baseViewerMode）和拾取意图（intent），动态解析出零件基准相关命令的可参与测量拾取的对象集合。
     * 作用：
     * - 支持零件环境下基准面、基准轴、基准点等多种参考类型的交互拾取需求；
     * - 根据当前是否为点击（click）或移入（hover）进行候选对象的过滤，确保拾取对象范围符合当前具体命令的业务要求；
     * - 当处于未知或默认模式时，则回退到默认的可拾取对象集，以保障交互完整性和防御异常模式。
     *
     * @param baseViewerMode 当前测量会话的底层交互模式，决定具体走哪类基准命令分支
     * @param intent 当前的拾取意图，'click' 表示点击拾取，'hover' 表示移入拾取
     * @returns 返回当前模式和操作意图下可用于后续测量命中的对象列表
     */
    private resolveReferenceTargets(
        baseViewerMode: ViewerMode,
        intent: 'click' | 'hover',
    ): THREE.Object3D[] {
        switch (baseViewerMode) {
            case ViewerMode.ReferencePlane:
                // 基准面命令：分派到 referencePlaneSelectionHandler
                return intent === 'click'
                    ? this.dependencies.referencePlaneSelectionHandler.collectMeasureClickSelectionTargets()
                    : this.dependencies.referencePlaneSelectionHandler.collectMeasureHoverSelectionTargets();
            case ViewerMode.ReferenceAxis:
                // 基准轴命令：分派到 referenceAxisSelectionHandler
                return intent === 'click'
                    ? this.dependencies.referenceAxisSelectionHandler.collectMeasureClickSelectionTargets()
                    : this.dependencies.referenceAxisSelectionHandler.collectMeasureHoverSelectionTargets();
            case ViewerMode.ReferencePoint:
                // 基准点命令：分派到 referencePointSelectionHandler
                return intent === 'click'
                    ? this.dependencies.referencePointSelectionHandler.collectMeasureClickSelectionTargets()
                    : this.dependencies.referencePointSelectionHandler.collectMeasureHoverSelectionTargets();
            default:
                // 其他模式：分派到默认选择处理器，确保防御性兜底
                return intent === 'click'
                    ? this.dependencies.defaultModeSelectionHandler.collectMeasureClickSelectionTargets()
                    : this.dependencies.defaultModeSelectionHandler.collectMeasureHoverSelectionTargets();
        }
    }

    /**
     * 解析装配体基准命令候选集。
     *
     * @param baseViewerMode 底层模式。
     * @param intent 拾取意图。
     * @returns 候选对象列表。
     */
    private resolveAssemblyReferenceTargets(
        baseViewerMode: ViewerMode,
        intent: 'click' | 'hover',
    ): THREE.Object3D[] {
        switch (baseViewerMode) {
            case ViewerMode.AssemblyReferencePlane:
                return intent === 'click'
                    ? this.dependencies.assemblyReferencePlaneSelectionHandler.collectMeasureClickSelectionTargets()
                    : this.dependencies.assemblyReferencePlaneSelectionHandler.collectMeasureHoverSelectionTargets();
            case ViewerMode.AssemblyReferenceAxis:
                return intent === 'click'
                    ? this.dependencies.assemblyReferenceAxisSelectionHandler.collectMeasureClickSelectionTargets()
                    : this.dependencies.assemblyReferenceAxisSelectionHandler.collectMeasureHoverSelectionTargets();
            case ViewerMode.AssemblyReferencePoint:
                return intent === 'click'
                    ? this.dependencies.assemblyReferencePointSelectionHandler.collectMeasureClickSelectionTargets()
                    : this.dependencies.assemblyReferencePointSelectionHandler.collectMeasureHoverSelectionTargets();
            default:
                return intent === 'click'
                    ? this.dependencies.assemblySelectionHandler.collectMeasureClickSelectionTargets()
                    : this.dependencies.assemblySelectionHandler.collectMeasureHoverSelectionTargets();
        }
    }

    /**
     * 解析测量拾取应委托的底层 Handler。
     *
     * @param baseViewerMode 测量会话底层模式。
     * @returns 模式对应的实体选择处理器。
     */
    private resolvePickHandler(baseViewerMode: ViewerMode): AbstractEntitySelectionHandler {
        const scopeFamily = resolveMeasurePickScopeFamily(baseViewerMode);

        switch (scopeFamily) {
            case MeasurePickScopeFamily.PartIdle:
                return this.dependencies.defaultModeSelectionHandler;
            case MeasurePickScopeFamily.Assembly:
                return this.dependencies.assemblySelectionHandler;
            case MeasurePickScopeFamily.Sketch:
                return this.dependencies.sketchSelectionHandler;
            case MeasurePickScopeFamily.Reference:
                return this.resolveReferencePickHandler(baseViewerMode);
            case MeasurePickScopeFamily.AssemblyReference:
                return this.resolveAssemblyReferencePickHandler(baseViewerMode);
            default:
                return this.dependencies.defaultModeSelectionHandler;
        }
    }

    /**
     * 解析零件基准命令拾取 Handler。
     *
     * @param baseViewerMode 底层模式。
     * @returns 实体选择处理器。
     */
    private resolveReferencePickHandler(baseViewerMode: ViewerMode): AbstractEntitySelectionHandler {
        switch (baseViewerMode) {
            case ViewerMode.ReferencePlane:
                return this.dependencies.referencePlaneSelectionHandler;
            case ViewerMode.ReferenceAxis:
                return this.dependencies.referenceAxisSelectionHandler;
            case ViewerMode.ReferencePoint:
                return this.dependencies.referencePointSelectionHandler;
            default:
                return this.dependencies.defaultModeSelectionHandler;
        }
    }

    /**
     * 解析装配体基准命令拾取 Handler。
     *
     * @param baseViewerMode 底层模式。
     * @returns 实体选择处理器。
     */
    private resolveAssemblyReferencePickHandler(baseViewerMode: ViewerMode): AbstractEntitySelectionHandler {
        switch (baseViewerMode) {
            case ViewerMode.AssemblyReferencePlane:
                return this.dependencies.assemblyReferencePlaneSelectionHandler;
            case ViewerMode.AssemblyReferenceAxis:
                return this.dependencies.assemblyReferenceAxisSelectionHandler;
            case ViewerMode.AssemblyReferencePoint:
                return this.dependencies.assemblyReferencePointSelectionHandler;
            default:
                return this.dependencies.assemblySelectionHandler;
        }
    }
}
